import React, { useCallback, useEffect, useState } from "react";
import {
  Backpack, Plus, Trash2, MoreHorizontal, Loader2, ChevronRight,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import BackpackList from "@/components/BackpackList";

/**
 * Admin-only "Backpack Lists" panel.
 *
 * Mirrors the per-trip Backpack UX but is scoped to the current
 * user's account and NOT tied to any booked trip. Two purposes:
 *   1) Personal / Business shopping-list management inside the app.
 *   2) End-to-end smoke test that the Amazon Associates tag
 *      (`travelbio-20`) reaches every Buy-on-Amazon link.
 *
 * Reuses `<BackpackList listId=… />` for the item panel so all
 * check / trash / Buy-on-Amazon interactions match the trip
 * flavour byte-for-byte.
 */

const TIDS = {
  panel: "admin-backpack-panel",
  newListBtn: "admin-backpack-new-list-btn",
  newListInput: "admin-backpack-new-list-input",
  createListBtn: "admin-backpack-create-list-btn",
  cancelCreateBtn: "admin-backpack-cancel-create-btn",
  listRow: (id) => `admin-backpack-list-row-${id}`,
  listMenu: (id) => `admin-backpack-list-menu-${id}`,
  renameBtn: (id) => `admin-backpack-list-rename-${id}`,
  deleteBtn: (id) => `admin-backpack-list-delete-${id}`,
  emptyCta: "admin-backpack-empty-cta",
};

function ListRowMenu({ list, onRename, onDelete, onClose }) {
  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest(`[data-list-menu-scope="${list.id}"]`)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [list.id, onClose]);
  return (
    <div
      data-list-menu-scope={list.id}
      className="absolute right-0 top-8 z-20 w-32 rounded-lg bg-white border-2 border-tb-cream-3 shadow-md overflow-hidden"
    >
      <button
        data-testid={TIDS.renameBtn(list.id)}
        onClick={() => { onRename(list); onClose(); }}
        className="w-full text-left px-3 py-2 font-hand text-sm text-tb-navy hover:bg-tb-cream tb-tap"
      >
        Rename
      </button>
      <button
        data-testid={TIDS.deleteBtn(list.id)}
        onClick={() => { onDelete(list); onClose(); }}
        className="w-full text-left px-3 py-2 font-hand text-sm text-tb-coral hover:bg-tb-cream tb-tap"
      >
        Delete
      </button>
    </div>
  );
}

export default function UserBackpackPanel() {
  const [lists, setLists] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/backpack/lists");
      const arr = Array.isArray(data?.lists) ? data.lists : [];
      setLists(arr);
      setSelectedId((prev) => {
        if (prev && arr.some((l) => l.id === prev)) return prev;
        return arr[0]?.id || null;
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load backpack lists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createList = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/backpack/lists", { name });
      setLists((xs) => [...xs, data]);
      setSelectedId(data.id);
      setCreating(false);
      setNewName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't create list");
    }
  };

  const renameList = async (list) => {
    // eslint-disable-next-line no-alert
    const next = window.prompt("Rename this Backpack list:", list.name);
    if (next == null) return;
    const name = next.trim();
    if (!name || name === list.name) return;
    try {
      const { data } = await api.patch(`/backpack/lists/${list.id}`, { name });
      setLists((xs) => xs.map((l) => (l.id === list.id ? data : l)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't rename list");
    }
  };

  const deleteList = async (list) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Delete "${list.name}" and every item inside it?`);
    if (!ok) return;
    try {
      await api.delete(`/backpack/lists/${list.id}`);
      const remaining = lists.filter((l) => l.id !== list.id);
      setLists(remaining);
      if (selectedId === list.id) {
        setSelectedId(remaining[0]?.id || null);
      }
      toast.success("List deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete list");
    }
  };

  const selected = lists.find((l) => l.id === selectedId) || null;

  return (
    <section
      data-testid={TIDS.panel}
      className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-4"
    >
      <header className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-xl bg-tb-gold/20 flex items-center justify-center">
          <Backpack className="w-5 h-5 text-tb-gold-bright" />
        </span>
        <div className="min-w-0">
          <h2 className="font-serif-it text-tb-navy text-2xl leading-tight">
            Backpack Lists
          </h2>
          <p className="font-hand text-tb-navy/60 text-xs italic">
            {"personal + business Amazon lists \u00B7 for owner smoke-testing"}
          </p>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 font-hand text-tb-navy/60 py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> {"loading lists\u2026"}
        </div>
      ) : lists.length === 0 && !creating ? (
        <div className="text-center py-8">
          <p className="font-hand text-tb-navy/60 italic mb-3">
            No Backpack lists yet.
          </p>
          <button
            data-testid={TIDS.emptyCta}
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap"
          >
            <Plus className="w-4 h-4" /> Create your first list
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-4">
          {/* Left: list picker */}
          <aside className="space-y-1.5">
            {lists.map((l) => {
              const active = l.id === selectedId;
              const boughtCount = (l.items || []).filter((i) => i.bought).length;
              return (
                <div key={l.id} className="relative">
                  <button
                    data-testid={TIDS.listRow(l.id)}
                    onClick={() => setSelectedId(l.id)}
                    className={
                      "w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition tb-tap " +
                      (active
                        ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                        : "bg-tb-cream-2 text-tb-navy border-tb-cream-3 hover:border-tb-gold/60")
                    }
                  >
                    <span className="flex-1 min-w-0 truncate font-ui text-sm">
                      {l.name}
                    </span>
                    <span
                      className={
                        "text-xs font-hand italic " +
                        (active ? "text-tb-gold/70" : "text-tb-navy/50")
                      }
                    >
                      {boughtCount}/{(l.items || []).length}
                    </span>
                    <ChevronRight
                      className={
                        "w-3.5 h-3.5 " +
                        (active ? "text-tb-gold-bright" : "text-tb-navy/40")
                      }
                    />
                  </button>
                  <button
                    data-testid={TIDS.listMenu(l.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId((prev) => (prev === l.id ? null : l.id));
                    }}
                    aria-label="List actions"
                    className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-white/20 tb-tap"
                    data-list-menu-scope={l.id}
                  >
                    <MoreHorizontal
                      className={
                        "w-4 h-4 " +
                        (l.id === selectedId ? "text-tb-gold-bright" : "text-tb-navy/50")
                      }
                    />
                  </button>
                  {openMenuId === l.id && (
                    <ListRowMenu
                      list={l}
                      onRename={renameList}
                      onDelete={deleteList}
                      onClose={() => setOpenMenuId(null)}
                    />
                  )}
                </div>
              );
            })}
            {creating ? (
              <form onSubmit={createList} className="pt-2 space-y-1.5">
                <input
                  data-testid={TIDS.newListInput}
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="list name"
                  className="w-full px-3 py-2 rounded-lg bg-tb-cream-2 border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
                />
                <div className="flex gap-2">
                  <button
                    data-testid={TIDS.createListBtn}
                    type="submit"
                    disabled={!newName.trim()}
                    className="flex-1 py-1.5 rounded-lg bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap disabled:opacity-50"
                  >
                    create
                  </button>
                  <button
                    data-testid={TIDS.cancelCreateBtn}
                    type="button"
                    onClick={() => { setCreating(false); setNewName(""); }}
                    className="px-3 rounded-lg border-2 border-tb-cream-3 text-tb-navy/70 font-hand text-sm tb-tap"
                  >
                    cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                data-testid={TIDS.newListBtn}
                onClick={() => setCreating(true)}
                className="w-full mt-2 flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 border-dashed border-tb-cream-3 text-tb-navy/70 hover:text-tb-navy hover:border-tb-gold font-hand text-sm tb-tap"
              >
                <Plus className="w-4 h-4" /> New List
              </button>
            )}
          </aside>

          {/* Right: items panel (reuses BackpackList in list mode) */}
          <div className="min-w-0">
            {selected ? (
              <BackpackList
                key={selected.id}
                listId={selected.id}
                mode="full"
              />
            ) : (
              <p className="font-hand text-tb-navy/55 italic text-center py-8">
                Select a list on the left, or create a new one.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
